/**
 * Dynamic OG Image utilities
 */

/**
 * Default OG / Twitter sharing image (F VILLAGE).
 * Mirrors the static `og:image` baked into `index.html`. Centralized so the
 * fallback URL is defined once across getters and the base Head component.
 */
export const DEFAULT_OG_IMAGE = 'https://booking.hkdballpark.com/img/ogp.png'

interface PropertyImageData {
  propertyId: string
  propertyName: string
  mainImage?: string
  images?: string[]
}

interface RoomImageData {
  roomId: string
  roomName: string
  propertyName: string
  mainImage?: string
  images?: string[]
  price?: number
}

/**
 * Get the best image for OG from property data
 */
export const getPropertyOgImage = (data: PropertyImageData): string => {
  // Priority: mainImage > first image in array > default
  if (data.mainImage) {
    return data.mainImage
  }

  if (data.images && data.images.length > 0) {
    return data.images[0]
  }

  return DEFAULT_OG_IMAGE
}

/**
 * Get the best image for OG from room data
 */
export const getRoomOgImage = (data: RoomImageData): string => {
  // Priority: mainImage > first image in array > property default > default
  if (data.mainImage) {
    return data.mainImage
  }

  if (data.images && data.images.length > 0) {
    return data.images[0]
  }

  return DEFAULT_OG_IMAGE
}

/**
 * Generate dynamic OG image URL for properties
 * This could be extended to use a service like Vercel OG or Cloudinary
 */
export const generateDynamicOgImage = (params: {
  title: string
  subtitle?: string
  imageUrl?: string
  price?: number
  logo?: string
}): string => {
  // For now, return the static image or provided image
  // In the future, this could generate dynamic images using:
  // 1. Vercel OG Image Generation
  // 2. Cloudinary URL transformations
  // 3. Custom image generation service

  if (params.imageUrl) {
    return params.imageUrl
  }

  return DEFAULT_OG_IMAGE
}

/**
 * Get image dimensions for better social media display
 */
export const getImageDimensions = (
  _imageUrl: string
): { width: string; height: string } =>
  // Default OG image dimensions (1200x630 is recommended)
  ({
    width: '1200',
    height: '630',
  })

/**
 * Ensure image URL is absolute
 */
export const ensureAbsoluteUrl = (
  url: string,
  baseUrl = 'https://booking.hkdballpark.com'
): string => {
  if (!url) return ''

  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url
  }

  if (url.startsWith('//')) {
    return `https:${url}`
  }

  if (url.startsWith('/')) {
    return `${baseUrl}${url}`
  }

  return `${baseUrl}/${url}`
}
